"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LayoutModule = void 0;
const tslib_1 = require("tslib");
const AdaptableModuleBase_1 = require("./AdaptableModuleBase");
const ModuleConstants = tslib_1.__importStar(require("../Utilities/Constants/ModuleConstants"));
const LayoutRedux = tslib_1.__importStar(require("../Redux/ActionsReducers/LayoutRedux"));
const ArrayExtensions_1 = tslib_1.__importDefault(require("../Utilities/Extensions/ArrayExtensions"));
const LayoutRadioSelector_1 = require("../View/Layout/LayoutRadioSelector");
const LayoutStatusBarSubPanelPopover_1 = require("../View/Layout/LayoutStatusBarSubPanelPopover");
const EditCurrentLayoutButton_1 = require("../View/Layout/EditCurrentLayoutButton");
const LayoutCloneButton_1 = require("../View/Layout/LayoutCloneButton");
const SaveLayoutButton_1 = require("../View/Layout/SaveLayoutButton");
const LayoutWizard_1 = require("../View/Layout/Wizard/LayoutWizard");
const getLayoutFilterViewItems_1 = require("./Utilities/Layout/getLayoutFilterViewItems");
const getLayoutSortViewItems_1 = require("./Utilities/Layout/getLayoutSortViewItems");
const AggregationColumns_1 = require("../PredefinedConfig/Common/AggregationColumns");
const windowFactory_1 = require("../View/Components/Popups/WindowPopups/windowFactory");
const flattenDeep_1 = tslib_1.__importDefault(require("lodash/flattenDeep"));
const StringExtensions_1 = tslib_1.__importDefault(require("../Utilities/Extensions/StringExtensions"));
const getGridFilterPreview_1 = require("../View/Layout/Wizard/getGridFilterPreview");
class LayoutModule extends AdaptableModuleBase_1.AdaptableModuleBase {
    constructor(api) {
        super(ModuleConstants.LayoutModuleId, ModuleConstants.LayoutFriendlyName, 'grid', 'LayoutPopup', 'Named sets of column visibility, order, groupings, aggregation, pivots etc.', api);
        this.clearUnsuportedFeaturesFromLayout = (layout) => {
            const layoutSupportedFeatures = this.api.layoutApi.internalApi.getLayoutSupportedFeatures();
            let needsUpdate = false;
            const layoutToSave = Object.assign({}, layout);
            if (!layoutSupportedFeatures.RowGroupedColumns && layout.RowGroupedColumns) {
                delete layoutToSave.RowGroupedColumns;
                needsUpdate = true;
            }
            if (!layoutSupportedFeatures.AggregationColumns && layout.AggregationColumns) {
                delete layoutToSave.AggregationColumns;
                needsUpdate = true;
            }
            if (!layoutSupportedFeatures.PivotColumns && layout.PivotColumns) {
                delete layoutToSave.PivotColumns;
                delete layoutToSave.EnablePivot;
                needsUpdate = true;
            }
            if (!layoutSupportedFeatures.ColumnFilters && layout.ColumnFilters) {
                delete layoutToSave.ColumnFilters;
                needsUpdate = true;
            }
            if (!layoutSupportedFeatures.GridFilter && layout.GridFilter) {
                delete layoutToSave.GridFilter;
                needsUpdate = true;
            }
            if (!layoutSupportedFeatures.ColumnSorts && layout.ColumnSorts) {
                delete layoutToSave.ColumnSorts;
                needsUpdate = true;
            }
            if (needsUpdate) {
                this.api.layoutApi.createOrUpdateLayout(layoutToSave);
            }
        };
        this.api.eventApi.on('LayoutChanged', (layoutChangedInfo) => {
            var _a;
            if (layoutChangedInfo.newLayoutState.CurrentLayout !==
                ((_a = layoutChangedInfo.oldLayoutState) === null || _a === void 0 ? void 0 : _a.CurrentLayout)) {
                // we are interested only if current layout was changed (is different from the previous one)
                this.handleLayoutChange();
            }
        });
    }
    updateOldConfig() {
        /**
         * Viewport mode does not support a few
         * features instead of complicating the
         * logic where layout is applied, it is easier and
         * less error prone to just remove it.
         */
        if (this.api.gridApi.getAgGridRowModelType() === 'viewport') {
            this.api.layoutApi.getLayouts().forEach(this.clearUnsuportedFeaturesFromLayout);
        }
    }
    getModuleAdaptableObjects() {
        return this.api.layoutApi.getLayouts();
    }
    getExplicitlyReferencedColumnIds(layout) {
        const columnIds = [];
        columnIds.push(...layout.Columns);
        if (layout.AggregationColumns) {
            columnIds.push(...Object.keys(layout.AggregationColumns));
        }
        if (layout.PivotColumns) {
            columnIds.push(...layout.PivotColumns);
        }
        if (layout.RowGroupedColumns) {
            columnIds.push(...layout.RowGroupedColumns);
        }
        return Array.from(new Set(columnIds));
    }
    getTeamSharingReferences(adaptableObject) {
        const teamSharingReferences = super.getTeamSharingReferences(adaptableObject);
        const layoutName = adaptableObject.Name;
        if (this.api.layoutApi.internalApi.hasLayoutSpecificObjects() && !!layoutName) {
            const layoutAssociatedObjectReferences = [];
            const loadConfig = {
                associatedWithLayout: layoutName,
            };
            // we ensured that there are layout specific objects, so all the "getAll*()" api methods will return only the objects available in the current layout
            this.api.alertApi.getAlertDefinitions(loadConfig).forEach((alertDefinition) => layoutAssociatedObjectReferences.push({
                Reference: alertDefinition,
                Module: 'Alert',
            }));
            this.api.customSortApi.getCustomSorts(loadConfig).forEach((customSort) => layoutAssociatedObjectReferences.push({
                Reference: customSort,
                Module: 'CustomSort',
            }));
            this.api.flashingCellApi.getFlashingCellDefinitions(loadConfig).forEach((flashingCell) => layoutAssociatedObjectReferences.push({
                Reference: flashingCell,
                Module: 'FlashingCell',
            }));
            this.api.formatColumnApi.getFormatColumns(loadConfig).forEach((formatColumn) => layoutAssociatedObjectReferences.push({
                Reference: formatColumn,
                Module: 'FormatColumn',
            }));
            this.api.plusMinusApi.getAllPlusMinus(loadConfig).forEach((plusMinusNudge) => layoutAssociatedObjectReferences.push({
                Reference: plusMinusNudge,
                Module: 'PlusMinus',
            }));
            this.api.shortcutApi.getShortcuts(loadConfig).forEach((shortcut) => layoutAssociatedObjectReferences.push({
                Reference: shortcut,
                Module: 'Shortcut',
            }));
            [
                ...this.api.scheduleApi.getReportSchedules(loadConfig),
                ...this.api.scheduleApi.getReminderSchedules(loadConfig),
                ...this.api.scheduleApi.getGlue42Schedules(loadConfig),
                ...this.api.scheduleApi.getIPushPullSchedules(loadConfig),
                ...this.api.scheduleApi.getOpenFinSchedules(loadConfig),
            ].forEach((schedule) => layoutAssociatedObjectReferences.push({
                Reference: schedule,
                Module: 'Schedule',
            }));
            teamSharingReferences.push(...layoutAssociatedObjectReferences);
        }
        return teamSharingReferences;
    }
    hasNamedQueryReferences() {
        return true;
    }
    handleAdaptableReady() {
        requestAnimationFrame(() => {
            this.api.layoutApi.internalApi.fireLayoutChangedEvent('ADAPTABLE_READY', null, this.api.layoutApi.getLayoutState());
        });
    }
    addColumnMenuItems(column) {
        var _a, _b;
        let returnColumnMenuItems = [];
        if (this.isModuleEditable()) {
            const isReadOnlyLayout = this.api.layoutApi.isCurrentLayoutReadOnly();
            if (!isReadOnlyLayout) {
                returnColumnMenuItems.push(this.createColumnMenuItemShowPopup('Edit Layout', this.moduleInfo.Popup, this.moduleInfo.Glyph, {
                    action: 'Edit',
                    source: 'ColumnMenu',
                    value: this.api.layoutApi.getCurrentLayout(),
                }));
            }
            if (column) {
                if (!isReadOnlyLayout) {
                    returnColumnMenuItems.push(this.createColumnMenuItemClickFunction('Change Caption', 'edit', () => this.api.layoutApi.showChangeColumnCaption(column)));
                }
                if (column.hideable) {
                    returnColumnMenuItems.push(this.createColumnMenuItemClickFunction('Hide Column', 'visibility-off-bold', () => {
                        this.api.columnApi.hideColumn(column.columnId);
                    }));
                }
            }
            let selectMenuItems = [];
            if (column) {
                const hasExistingSelection = (_b = (_a = this.api.gridApi.getSelectedCellInfo()) === null || _a === void 0 ? void 0 : _a.gridCells) === null || _b === void 0 ? void 0 : _b.length;
                if (hasExistingSelection) {
                    selectMenuItems.push(this.createColumnMenuItemClickFunction('Column (Preserve Selection)', 'column-add', () => {
                        this.api.columnApi.addColumnToSelection(column.columnId);
                    }));
                    selectMenuItems.push(this.createColumnMenuItemClickFunction('Column (Reset Selection)', 'columns', () => {
                        this.api.columnApi.selectColumn(column.columnId);
                    }));
                }
                else {
                    selectMenuItems.push(this.createColumnMenuItemClickFunction('Column', 'columns', () => {
                        this.api.columnApi.selectColumn(column.columnId);
                    }));
                }
            }
            selectMenuItems.push(this.createColumnMenuItemClickFunction('Whole Grid', this.moduleInfo.Glyph, () => {
                this.api.gridApi.selectAll();
            }));
            returnColumnMenuItems.push({
                label: 'Select',
                isVisible: true,
                module: this.moduleInfo.ModuleName,
                icon: {
                    name: 'tab-unselected',
                },
                subItems: selectMenuItems,
            });
        }
        return returnColumnMenuItems;
    }
    addContextMenuItems(menuContext) {
        let returnColumnMenuItems = [];
        if (this.isModuleEditable() && !this.api.layoutApi.isCurrentLayoutReadOnly()) {
            returnColumnMenuItems.push(this.createColumnMenuItemShowPopup('Edit Layout', this.moduleInfo.Popup, this.moduleInfo.Glyph, {
                action: 'Edit',
                source: 'ColumnMenu',
            }));
        }
        if ((menuContext.selectedCellInfo &&
            ArrayExtensions_1.default.IsNotNullOrEmpty(menuContext.selectedCellInfo.columns)) ||
            (menuContext.selectedRowInfo &&
                ArrayExtensions_1.default.IsNotNullOrEmpty(menuContext.selectedRowInfo.gridRows))) {
            let clickFunction = () => {
                this.api.gridApi.deselectAll();
            };
            returnColumnMenuItems.push(this.createColumnMenuItemClickFunction('Clear Selected Cells', 'undo', clickFunction));
            if (this.isModuleAvailable()) {
                let gridMenuItems = [];
                let autoSizeFunction = () => {
                    this.api.columnApi.autosizeAllColumns();
                };
                gridMenuItems.push(this.createColumnMenuItemClickFunction('Auto Size', 'arrow-expand', autoSizeFunction));
                let clickFunction = () => {
                    this.api.gridApi.selectAll();
                };
                gridMenuItems.push(this.createColumnMenuItemClickFunction('Select', 'tab-unselected', clickFunction));
                returnColumnMenuItems.push({
                    label: 'Grid',
                    module: this.moduleInfo.ModuleName,
                    isVisible: true,
                    icon: { name: 'align-justify' },
                    subItems: gridMenuItems,
                });
            }
        }
        const viewPivotItemsMenuItem = this.createViewPivotItemsMenuItem(menuContext);
        if (viewPivotItemsMenuItem) {
            returnColumnMenuItems.push(viewPivotItemsMenuItem);
        }
        return returnColumnMenuItems;
    }
    createViewPivotItemsMenuItem(menuContext) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        // current group => menuContext.rowNode.field;
        if (((_b = (_a = menuContext.selectedCellInfo) === null || _a === void 0 ? void 0 : _a.gridCells) === null || _b === void 0 ? void 0 : _b.length) !== 1) {
            return;
        }
        const currentLayout = this.api.layoutApi.getCurrentLayout();
        if (!currentLayout.EnablePivot) {
            return;
        }
        const { agGridColumn } = menuContext;
        const columnId = agGridColumn.getColId();
        const isPivotColumn = this.api.columnApi.isAutoPivotColumn(columnId);
        if (!isPivotColumn && this.api.columnApi.getColumnDataTypeForColumnId(columnId) !== 'Number') {
            return;
        }
        /**
         * Cannot base the values on the pivot column id, because the value or the field may have underscrores
         *
         *
         * e.g. pivot_license_MIT License_stargazers_count
         */
        // pivot_status_Booked_price
        // price
        const aggColumn = (_f = (_e = (_d = (_c = agGridColumn === null || agGridColumn === void 0 ? void 0 : agGridColumn.getColDef) === null || _c === void 0 ? void 0 : _c.call(agGridColumn).pivotValueColumn) === null || _d === void 0 ? void 0 : _d.getColDef) === null || _e === void 0 ? void 0 : _e.call(_d)) === null || _f === void 0 ? void 0 : _f.field;
        // Booked
        const pivotValue = (_j = (_h = (_g = agGridColumn === null || agGridColumn === void 0 ? void 0 : agGridColumn.getColDef) === null || _g === void 0 ? void 0 : _g.call(agGridColumn)) === null || _h === void 0 ? void 0 : _h.pivotKeys) === null || _j === void 0 ? void 0 : _j[0];
        // status - the preview works only with one column
        const pivotColumnId = (_k = currentLayout === null || currentLayout === void 0 ? void 0 : currentLayout.PivotColumns) === null || _k === void 0 ? void 0 : _k[0];
        function getData(rows) {
            return [
                ...rows.map((row) => {
                    if (row.childrenAfterFilter) {
                        return getData(row.childrenAfterFilter);
                    }
                    return row.data;
                }),
            ];
        }
        const data = (0, flattenDeep_1.default)(getData(menuContext.rowNode.childrenAfterFilter));
        const rowGroups = currentLayout.RowGroupedColumns;
        const cellValue = isPivotColumn
            ? menuContext.rowNode.aggData[columnId]
            : menuContext.gridCell.displayValue;
        const popupProps = {
            hasPivotValue: isPivotColumn,
            columnId,
            rowGroups,
            rows: data,
            layout: currentLayout,
            aggColumn,
            pivotValue,
            pivotColumnId,
        };
        return {
            module: this.moduleInfo.ModuleName,
            isVisible: true,
            label: 'Expand Aggregated Value',
            onClick: () => {
                this.api.internalApi.showPopupWindow({
                    // force only one window instance opened
                    id: windowFactory_1.SHOW_PIVOT_COLUMN_DETAILS,
                    factoryId: windowFactory_1.SHOW_PIVOT_COLUMN_DETAILS,
                    title: `Rows for Aggregated Value: ${cellValue}`,
                    icon: undefined,
                    popupProps,
                });
            },
            icon: {
                name: 'arrow-expand',
            },
        };
    }
    getTeamSharingAction() {
        return {
            ModuleEntities: this.api.layoutApi.getLayouts(),
            AddAction: LayoutRedux.LayoutAdd,
            EditAction: LayoutRedux.LayoutSave,
        };
    }
    toViewAll() {
        return this.getModuleAdaptableObjects().map((layout) => this.toView(layout));
    }
    toView(layout) {
        var _a, _b, _c, _d, _e, _f;
        const maxColumnsToDisplay = (_b = (_a = this.api.optionsApi.getLayoutOptions().layoutViewOptions) === null || _a === void 0 ? void 0 : _a.maxColumnsToDisplay) !== null && _b !== void 0 ? _b : 10;
        const columnIdToFriendlyName = (columnId) => {
            return this.api.columnApi.getFriendlyNameForColumnId(columnId);
        };
        let columns = layout.Columns.filter((c) => !this.api.columnApi.isAutoRowGroupColumn(c)).filter((c) => !this.api.columnApi.isAutoPivotColumn(c));
        if (columns.length >
            maxColumnsToDisplay + 1 /* +1 is to show tag only beginning with 2, 'other 2' */) {
            const extraColumns = columns.length - maxColumnsToDisplay;
            const firstNColumns = columns
                .slice(0, maxColumnsToDisplay)
                .map((column) => columnIdToFriendlyName(column));
            columns = [...firstNColumns, `and other ${extraColumns}`];
        }
        else {
            columns = columns.map((column) => columnIdToFriendlyName(column));
        }
        return {
            items: [
                {
                    name: 'Name',
                    values: [layout.Name],
                },
                {
                    name: 'Layout Grid Type',
                    values: [(layout === null || layout === void 0 ? void 0 : layout.EnablePivot) ? 'Pivot' : 'Table'],
                },
                /**
                 * Hide pivot columns.
                 *
                 * The generated pivot columns cannot be prevented from beeing on the layout object.
                 * When they are removed/not added, they no longer apear in the grid.
                 */
                !layout.EnablePivot && {
                    name: 'Columns',
                    values: columns,
                },
                ((_c = layout.ColumnSorts) === null || _c === void 0 ? void 0 : _c.length) && (0, getLayoutSortViewItems_1.getLayoutSortViewItems)(layout, this.api),
                ((_d = layout === null || layout === void 0 ? void 0 : layout.ColumnFilters) === null || _d === void 0 ? void 0 : _d.length) && (0, getLayoutFilterViewItems_1.getLayoutFilterViewItems)(layout, this.api),
                ((_e = layout === null || layout === void 0 ? void 0 : layout.RowGroupedColumns) === null || _e === void 0 ? void 0 : _e.length) && {
                    name: 'Grouped Columns',
                    values: layout.RowGroupedColumns.map((colId) => columnIdToFriendlyName(colId)),
                },
                ((_f = layout === null || layout === void 0 ? void 0 : layout.PivotColumns) === null || _f === void 0 ? void 0 : _f.length) && {
                    name: 'Pivot Columns',
                    values: layout.PivotColumns.map((colId) => columnIdToFriendlyName(colId)),
                },
                (layout === null || layout === void 0 ? void 0 : layout.AggregationColumns) &&
                    Object.keys(layout === null || layout === void 0 ? void 0 : layout.AggregationColumns).length && {
                    name: 'Aggregation Columns',
                    values: Object.entries(layout === null || layout === void 0 ? void 0 : layout.AggregationColumns)
                        .map(([colId, aggFn]) => {
                        if (colId === 'Source' || colId === 'Uuid') {
                            return '';
                        }
                        let aggFnName = '';
                        if (typeof aggFn === 'string') {
                            aggFnName = aggFn;
                        }
                        else if (typeof aggFn === 'object' && aggFn.type === 'weightedAverage') {
                            aggFnName = AggregationColumns_1.WEIGHTED_AVERAGE_AGG_FN_NAME;
                        }
                        return `${aggFnName}(${columnIdToFriendlyName(colId)})`;
                    })
                        .filter(Boolean),
                },
                (layout === null || layout === void 0 ? void 0 : layout.GridFilter) &&
                    StringExtensions_1.default.IsNotNullOrEmpty(layout.GridFilter.Expression) &&
                    (0, getGridFilterPreview_1.getGridFilterViewItems)(layout),
            ].filter(Boolean),
            abObject: layout,
        };
    }
    getViewProperties() {
        return {
            actions: [LayoutRadioSelector_1.LayoutRadioSelector, LayoutCloneButton_1.LayoutCloneButton],
            getDeleteAction: (layout) => {
                // make sure we do not delete the last layout
                if (this.getModuleAdaptableObjects().length === 1) {
                    return null;
                }
                return LayoutRedux.LayoutDelete(layout);
            },
            getEditWizard: () => LayoutWizard_1.LayoutWizard,
            getStatusBarPanelProps: () => {
                return {
                    content: LayoutStatusBarSubPanelPopover_1.LayoutStatusbar,
                    extraActions: [EditCurrentLayoutButton_1.EditCurrentLayoutButton, SaveLayoutButton_1.SaveLayoutButton],
                };
            },
        };
    }
    handleLayoutChange() {
        if (!this.api.layoutApi.internalApi.hasLayoutSpecificObjects()) {
            // no need if no callback implementation is provided or no auto checking
            return;
        }
        // PlusMinus
        this.api.internalApi
            .getModuleService()
            .getModuleById('PlusMinus')
            .checkListenToKeyDown();
        // Shortcut
        this.api.internalApi
            .getModuleService()
            .getModuleById('Shortcut')
            .checkListenToKeyDown();
        // Schedule
        this.api.internalApi
            .getModuleService()
            .getModuleById('Schedule')
            .setUpScheduleJobs();
        // CustomSort, FlashingCell, FormatColumn
        // we need to refresh the column defs, as some configs may have changed
        this.api.internalApi.getAdaptableInstance().setupColumns();
    }
    getReferencedNamedQueryNames(layout) {
        var _a;
        if (!((_a = layout.GridFilter) === null || _a === void 0 ? void 0 : _a.Expression)) {
            return [];
        }
        return this.api.namedQueryApi.internalApi.getReferencedNamedQueryNames(layout.GridFilter.Expression);
    }
}
exports.LayoutModule = LayoutModule;
